import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isAfter, isValid } from 'date-fns';
import {
	Box,
	Button,
	Container,
	Flex,
	HStack,
	Text,
	VStack,
} from 'src/components/ui';
import { InfoIcon } from '@chakra-ui/icons';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { debounce, isEmpty, isEqual } from 'lodash';

import { CampaignContext } from 'src/contexts';
import { wasRecentBudgetUpdate } from 'src/hooks/useBudgetManager';
import {
	createOrUpdateCampaign,
	publishCampaignChannelsAndWait,
} from 'src/services/campaign';
import { toastError } from 'src/services/toast';
import { getScheduleDelta } from 'src/components/campaign/utils/delta';
import BudgetBreakdown from './BudgetBreakdown';
import CurrencyInputHook from '../CurrencyInput/CurrencyInputHook';
import {
	ECampaignScheduleType,
	ICampaignBudget,
	ICampaignSchedule,
	ICreative,
	IDesignDirection,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import LaunchCampaign from '../LaunchCampaign';
import { IChannelGroup } from 'src/lib/schemas';
import { CampaignScheduleSchema } from 'src/lib/schemas/campaign/form';
import DatePickerInputHook from 'src/components/common/date-picker/DatePickerInputHook';
import FusionLoading from 'src/components/common/FusionLoading';
import { delay } from 'src/lib/utils';
import { countPendingCreativesByChannel } from '../utils/countPendingCreativesByChannel';

interface CampaignScheduleProps {
	schedule?: Partial<ICampaignSchedule>;
	onScheduleSubmit: (data: ICampaignSchedule) => void;
	availableChannelGroups: IChannelGroup[] | null;
	isValidCampaignConfig: boolean;
	requirementsLabelStyle?: React.CSSProperties;
	loadingCreatives?: ConstrainBoolean;
	designDirections: IDesignDirection[];
	creatives: ICreative[];
	selectedChannels: string[];
	isBudgetLoading?: boolean;
}

const CampaignSchedule: FC<CampaignScheduleProps> = ({
	schedule,
	onScheduleSubmit,
	availableChannelGroups,
	isValidCampaignConfig,
	requirementsLabelStyle,
	selectedChannels,
	designDirections,
	creatives,
	isBudgetLoading = false,
}) => {
	const {
		campaign,
		setCampaign,
		id: campaignId,
		completeStep,
		isLaunchingRef,
	} = useContext(CampaignContext);
	const [isSaving, setIsSaving] = useState(false);
	const [isLaunchInitiated, setIsLaunchInitiated] = useState(false);
	const [showCreativesLoader, setShowCreativesLoader] = useState(false);
	const [pendingCreativesCount, setPendingCreativesCount] = useState(0);
	// Default to true - BudgetBreakdown will set to false if there are validation errors
	// If no paid channels or no budget needed, this stays true
	const [isValidCampaignBudget, setIsValidCampaignBudget] = useState(true);
	const [previousBudget, setPreviousBudget] = useState(campaign?.budget);
	const [currentBudget, setCurrentBudget] = useState<
		ICampaignBudget | undefined
	>(campaign?.budget || undefined);
	const timeoutId = useRef<NodeJS.Timeout | null>(null);
	const formMethods = useForm<ICampaignSchedule>({
		resolver: zodResolver(CampaignScheduleSchema),
	});

	const { reset, setValue, handleSubmit, control } = formMethods;
	const watchScheduleType = useWatch({ control, name: 'type' });
	const watchedStartDate = useWatch({ control, name: 'startDate' });
	const watchedEndDate = useWatch({ control, name: 'endDate' });
	const prevStartDateRef = useRef<number | undefined>();
	const prevEndDateRef = useRef<number | undefined>();

	const hasPaidChannels = useMemo(() => {
		const paidPlacements = availableChannelGroups
			?.flatMap((group) => group.channels)
			.flatMap((channel) => channel.placements)
			.filter((placement: any) => placement.apportion > 0)
			.map((placement: any) => placement.id);

		return campaign?.placements
			?.filter((placement) => placement !== 'twitterSocialMedia')
			.some((placement) => paidPlacements?.includes(placement));
	}, [campaign?.placements, availableChannelGroups]);

	useEffect(() => {
		if (!isEqual(previousBudget, campaign?.budget)) {
			// Don't override currentBudget if there was a recent budget update from CurrencyInputHook
			const hasRecentBudgetUpdate = wasRecentBudgetUpdate(2000); // 2 second window

			if (!hasRecentBudgetUpdate) {
				setCurrentBudget(campaign?.budget || undefined);
			}

			setPreviousBudget(campaign?.budget || undefined);
		}
	}, [campaign?.budget]);

	useEffect(() => {
		if (watchScheduleType === ECampaignScheduleType.Evergreen) {
			setValue('endDate', undefined, { shouldValidate: true });
		}
	}, [watchScheduleType, setValue]);

	useEffect(() => {
		if (watchedStartDate) {
			const startTime = new Date(watchedStartDate).getTime();
			if (
				prevStartDateRef.current !== startTime &&
				isValid(new Date(watchedStartDate))
			) {
				formMethods.trigger('startDate');
				prevStartDateRef.current = startTime;
			}
		}
		if (watchedEndDate) {
			const endTime = new Date(watchedEndDate).getTime();
			if (prevEndDateRef.current !== endTime) {
				formMethods.trigger('endDate');
				prevEndDateRef.current = endTime;
			}
		}
	}, [watchedStartDate, watchedEndDate, formMethods]);

	useEffect(() => {
		if (schedule) {
			reset({
				type: schedule.type,
				startDate: schedule.startDate
					? new Date(schedule.startDate)
					: new Date(),
				endDate: schedule.endDate ? new Date(schedule.endDate) : undefined,
			});
		}
	}, [schedule, reset]);

	useEffect(() => {
		const count = countPendingCreativesByChannel(campaign, creatives);
		setPendingCreativesCount(count);
	}, [campaign]);

	useEffect(() => {
		setIsSaving(true);
		debouncedAutoSave();
		return () => {
			debouncedAutoSave.cancel();
		};
	}, [watchScheduleType, watchedStartDate, watchedEndDate]);

	const handleAutoSaveSchedule = async () => {
		if (!campaign) return;

		// Don't auto-save if type is not set yet
		if (!watchScheduleType) {
			setIsSaving(false);
			return;
		}

		const newSchedule = {
			type: watchScheduleType,
			startDate: watchedStartDate ? new Date(watchedStartDate) : undefined,
			endDate:
				watchScheduleType === ECampaignScheduleType.Evergreen
					? undefined
					: watchedEndDate
						? new Date(watchedEndDate)
						: undefined,
		};

		const delta = getScheduleDelta(
			{
				type: schedule?.type ?? ECampaignScheduleType.Evergreen,
				startDate: schedule?.startDate
					? new Date(schedule.startDate)
					: undefined,
				endDate: schedule?.endDate ? new Date(schedule.endDate) : undefined,
			},
			newSchedule,
		);

		const isInvalidEndDate =
			newSchedule.endDate &&
			newSchedule.startDate &&
			isAfter(newSchedule.startDate, newSchedule.endDate);

		if (isEmpty(delta) || isInvalidEndDate) {
			setIsSaving(false);
			return;
		}

		//@ts-ignore
		setCampaign((prevCampaign) => ({
			...prevCampaign,
			schedule: newSchedule,
		}));
		try {
			await createOrUpdateCampaign(
				{
					schedule: {
						type: newSchedule.type,
						startDate: newSchedule.startDate ?? new Date(),
						endDate: newSchedule.endDate,
					},
				},
				campaign.id,
			);
		} catch (error) {
			toastError('Error saving schedule. Please try again.');
		} finally {
			setIsSaving(false);
		}
	};

	const debouncedAutoSave = debounce(() => {
		handleAutoSaveSchedule();
	}, 200);

	const handleScheduleSubmit = handleSubmit((data) => {
		clearTimeout(timeoutId.current as NodeJS.Timeout);
		// Don't auto-complete Step 3 here - let updateStepsAndSections handle it
		// based on actual campaign state (published or scheduled)
		// completeStep(2);
		onScheduleSubmit(data);
	});

	useEffect(() => {
		if (!isSaving && isLaunchInitiated) {
			(async () => await handleLaunch())();
		}
	}, [isSaving, isLaunchInitiated]);

	const handleLaunch = async () => {
		if (!campaignId || isLaunchingRef.current) return;
		isLaunchingRef.current = true;
		setIsLaunchInitiated(true);

		while (isSaving) {
			await delay(500);
		}

		setIsLaunchInitiated(false);
		isLaunchingRef.current = false;

		const placements = campaign?.placements ?? [];

		const response = await publishCampaignChannelsAndWait(
			campaignId,
			placements,
		);

		return response;
	};

	const handleChangeScheduleType = (type: ECampaignScheduleType) => {
		setValue('type', type);
		type === ECampaignScheduleType.Evergreen && setValue('endDate', undefined);
	};

	return (
		<Container w="full" maxW="full" px={{ base: 0, md: 4 }}>
			<Flex direction="column" gap={{ base: 6, md: 10 }} minH="155px">
				{showCreativesLoader ? (
					<Box textAlign="center" h="250px">
						<FusionLoading isLoading={showCreativesLoader ?? false} />
						{pendingCreativesCount > 0 ? (
							<Text size="md" mt={8}>
								Please wait while we generate the content for{' '}
								{pendingCreativesCount}{' '}
								{pendingCreativesCount < 2 ? 'channel' : 'channels'}
							</Text>
						) : (
							<Text size="md" mt={8}>
								Please wait while we generate the content for your channels
							</Text>
						)}
					</Box>
				) : (
					<FormProvider {...formMethods}>
						<form onSubmit={handleScheduleSubmit}>
							<Flex
								direction={{ base: 'column', md: 'row' }}
								gap={{ base: 4, md: 20 }}
								alignItems={{ base: 'stretch', md: 'flex-start' }}
							>
								<VStack gap={0} alignItems="left" flex={{ base: '1', md: 'initial' }}>
									<Text color="black" fontWeight={500} fontSize={16}>
										Schedule
									</Text>
									<Text
										fontSize="12px"
										color="#959595"
										style={requirementsLabelStyle}
									>
										Define the period of this campaign
									</Text>
								</VStack>
								<Flex
									flex={{ base: '1', md: 2 }}
									gap={4}
									ml={{ base: 0, md: '104px' }}
									direction={{ base: 'column', md: 'row' }}
									w={{ base: '100%', md: 'auto' }}
								>
									<Box flex={1}>
										<DatePickerInputHook
											name="startDate"
											value={watchedStartDate}
											label="Start Date"
											placeholder="Select date"
										/>
									</Box>
									<Box flex={1}>
										<DatePickerInputHook
											name="endDate"
											value={watchedEndDate}
											placeholder="Select date"
											isEndDate
											onChangeScheduleType={handleChangeScheduleType}
										/>
									</Box>
								</Flex>
							</Flex>

							{hasPaidChannels && (
								<Flex direction="column" gap={1} my={{ base: 4, md: 6 }}>
									<CurrencyInputHook
										name="budget.total"
										label="Budget"
										requirementsLabel="Total budget you want to spend in this campaign"
										requirementsLabelStyle={{
											color: '#718096',
											fontSize: '14px',
										}}
										placeholder="$0"
										direction="row"
										setCurrentBudget={setCurrentBudget}
										availableChannelGroups={availableChannelGroups}
										setIsSaving={setIsSaving}
									/>

									{/* Conditional helper text based on schedule type */}
									{watchScheduleType === ECampaignScheduleType.Evergreen && !watchedEndDate && (
										<Flex
											alignItems="flex-start"
											gap={2}
											p={3}
											bg="blue.50"
											borderRadius="md"
											border="1px solid"
											borderColor="blue.100"
											mt={2}
										>
											<InfoIcon color="blue.500" boxSize={4} mt={0.5} flexShrink={0} />
											<Text fontSize="sm" color="gray.700">
												This will be your monthly budget since no end date is set. Your campaign will run continuously.
											</Text>
										</Flex>
									)}
								</Flex>
							)}

							{hasPaidChannels &&
								currentBudget?.total !== undefined &&
								!isBudgetLoading && (
									<BudgetBreakdown
										key={`budget-${currentBudget?.total || 0}`}
										budget={currentBudget}
										availableChannelGroups={availableChannelGroups}
										setIsValidCampaignBudget={setIsValidCampaignBudget}
									/>
								)}
							<Box display="none" textAlign="center">
								<Button
									id="campaign-schedule-form-submit"
									colorScheme="secondary"
									fontWeight="medium"
									type="submit"
								/>
							</Box>
						</form>
					</FormProvider>
				)}
			</Flex>
			<Box hidden={showCreativesLoader ? true : false}>
				<LaunchCampaign
					isValidCampaignConfig={isValidCampaignConfig}
					selectedChannels={selectedChannels}
					isValidCampaignBudget={isValidCampaignBudget}
					onLaunch={async () => {
						if (!campaignId) {
							throw new Error('No campaign ID found');
						}
						return publishCampaignChannelsAndWait(
							campaignId,
							campaign?.placements ?? [],
						);
					}}
					setShowCreativesLoader={setShowCreativesLoader}
					pendingCreativesCount={pendingCreativesCount}
					designDirections={designDirections}
					creatives={creatives}
					setPendingCreativesCount={setPendingCreativesCount}
				/>
			</Box>
		</Container>
	);
};

export default CampaignSchedule;
